/**
 * Web provider handoff marker (cancel journey H).
 *
 * On web, "Open {provider}" leaves the page (`window.location.assign`), so the
 * journey cannot watch for the return. Before leaving it parks the intent
 * here; when the app loads again the web subscription coordinator re-opens H
 * in its "Let's check your plan." state and syncs. Only a synced snapshot
 * decides what happened (never the return itself). Session-scoped and short
 * lived; never holds anything but the intent, tier and account.
 */
import type { ProviderIntent } from './subscription-journey/cancel-journey';

const STORAGE_KEY = 'tabbio:subscription:provider-handoff';
const MAX_AGE_MS = 60 * 60 * 1000;

export interface ProviderHandoff {
  accountId: string;
  intent: ProviderIntent;
  tier: 'plus' | 'pro';
  createdAt: number;
}

/** Intents whose outcome is worth checking on return ("manage" is not). */
export function shouldParkHandoff(intent: ProviderIntent) {
  return intent !== 'manage';
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function writeProviderHandoff(handoff: Omit<ProviderHandoff, 'createdAt'>) {
  if (!shouldParkHandoff(handoff.intent)) return;
  try {
    storage()?.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...handoff, createdAt: Date.now() } satisfies ProviderHandoff),
    );
  } catch {
    // Private mode: the person can still check from Plan & billing.
  }
}

export function readProviderHandoff(
  accountId: string | null | undefined,
  now = Date.now(),
): ProviderHandoff | null {
  if (!accountId) return null;
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<ProviderHandoff>;
    const fresh =
      typeof value.createdAt === 'number' && now - value.createdAt <= MAX_AGE_MS;
    if (
      !fresh ||
      value.accountId !== accountId ||
      (value.tier !== 'plus' && value.tier !== 'pro') ||
      typeof value.intent !== 'string' ||
      !shouldParkHandoff(value.intent as ProviderIntent)
    ) {
      clearProviderHandoff();
      return null;
    }
    return value as ProviderHandoff;
  } catch {
    clearProviderHandoff();
    return null;
  }
}

export function clearProviderHandoff() {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
